import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, UntypedFormGroup, Validators} from "@angular/forms";
import {EventService} from "../services/event.service";
import {ActivatedRoute, Router} from "@angular/router";
import {DateValidator} from "../models/datevalidator";
import Sortable from "sortablejs";
import {ACCESSIBILITY_TAGS} from "../constants/accessibility-tags";
import {CATEGORIES} from "../constants/categories";
import {GeocodingService} from "../services/geocoding.service";
import {Event} from "../models/event";
import {ImageService} from "../services/image.service";
import { DocumentData, DocumentReference } from "@angular/fire/firestore";

@Component({
    selector: 'app-event-create',
    templateUrl: './event-create.component.html',
    styleUrls: ['./event-create.component.css'],
    standalone: false
})
export class EventCreateComponent implements OnInit {
  @ViewChild('topOfPage') topOfPage!: ElementRef;
  eventForm = new UntypedFormGroup({});
  currentStep = 1;

  // Variables used for loading and editing an existing event
  postId!: string | null;
  postOrgRef!: DocumentReference<DocumentData>;

  // Variables based on constants
  accessibilityTags = ACCESSIBILITY_TAGS;
  categories = CATEGORIES;

  // Variables used for image uploading
  images: Array<any> = new Array(5).fill(null); // Initialize with 5 empty slots
  imgUploadError: string = '';

  // Variables used for error handling in event creation
  categoryError: string = '';
  submitError: string = '';
  firstStepSubmitted: boolean = false;
  secondStepSubmitted: boolean = false;
  submitDisabled: boolean = false;

  // Variables used to determine what dates an event can be created between
  minDate = new Date().toISOString().split("T")[0];
  maxDate = '2030-12-31';

  constructor(
    private fb: FormBuilder,
    private eventService: EventService,
    private geocodeService: GeocodingService,
    private imgService: ImageService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    // Dynamically initializes the accessibility checkboxes.
    const accessibilityChecks = ACCESSIBILITY_TAGS.reduce(
      (controls, tag) => {
        controls[`${tag.tag}Check`] = [false];
        return controls;
    }, {} as { [key: string]: Boolean[] });

    this.eventForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(4)]],
      description: ['', [Validators.required, Validators.minLength(50)]],
      category: ['', [Validators.required]],
      ...accessibilityChecks,
      zipcode: ['', [Validators.required, Validators.pattern('^[1-9][0-9]{3}\\s?[a-zA-Z]{2}$')]],
      houseNumber: ['', [Validators.required, Validators.pattern('^[1-9]\\d*(?: ?(?:[a-zA-Z]|[/-] ?\\d+[a-zA-Z]?))?$')]],
      streetName: ['', [Validators.required, Validators.minLength(1)]],
      location: ['', [Validators.required, Validators.minLength(2)]],
      startTime: ['', [Validators.required, Validators.minLength(1)]],
      endTime: ['', [Validators.required, Validators.minLength(1)]],
      activityDate: ['', [Validators.required, DateValidator.inPast, DateValidator.beforeDate('2030-12-31')]],
      parts: this.fb.array([this.newPart()])
    });
  }

  ngOnInit(): void {
    const imgContainer = document.getElementById('draggable-container');

    if (imgContainer) {
      Sortable.create(imgContainer, {
        onMove: (evt) => {
          return !evt.related.classList.contains('empty');
        },
        onEnd: (evt) => {
          const oldIndex = evt.oldIndex ?? 0;
          const newIndex = evt.newIndex ?? 0;

          if (oldIndex !== newIndex) {
            const movedImage = this.images.splice(oldIndex, 1)[0];
            this.images.splice(newIndex, 0, movedImage);
          }
        }
      });
    }

    // Check if there's an ID in the route
    this.route.paramMap.subscribe(params => {
      this.postId = params.get('id');
      if (this.postId) {
        this.eventService.getEventByRef(this.postId).subscribe(event => {
          this.loadPost(event);
        });
      }
    });
  }

  /**
   * Function that triggers when users uploads files at the last part of the event creation and editing form.
   * It checks whether there's enough slots left to upload the selected images and if so, adds the uploaded images to the image array.
   *
   * @param file Image to be uploaded
   */
  uploadImage(file: any) {
    const element = file.currentTarget as HTMLInputElement;
    const files: FileList = file.target.files;

    // Process files only if they don't exceed the available slots
    if (files.length + this.getCurrentImageCount() > 5) {
      this.imgUploadError = "Je kunt maximaal 5 foto's uploaden.";
      return;
    }

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.addImage({ src: e.target.result, file, id: this.getNextAvailableIndex() });
      };
      reader.readAsDataURL(file);
    });

    this.imgUploadError = '';
    element.value = '';
  }

  /**
   * Changes the current event create step on the form.
   * Different parts of the event form are shown based on the currentStep variable.
   *
   * Upon clicking 'Volgende stap' on the event form, the input fields get validated and errors get shown if present.
   *
   * @param nextStep
   * @param currentStep
   */
  changeStep(nextStep: boolean, currentStep: number) {
    if (nextStep) {
      switch (currentStep) {
        case 1:
          this.firstStepSubmitted = true;

          let e1 = this.name?.errors;
          let e2 = this.description?.errors;
          let e3 = this.category?.errors;

          let checkCount = 0;

          this.accessibilityTags.forEach(accessibilityTag => {
            if (this.eventForm.get(`${accessibilityTag.tag}Check`)?.value) {
              checkCount++;
            }
          });

          if (checkCount >= 1) {
            this.categoryError = '';
            if (!e1 && !e2 && !e3) {
              this.currentStep++;
              this.scrollToTop();
            }
          } else {
            this.categoryError = 'Je moet minimaal 1 doelgroep selecteren.';
          }
          break;
        case 2:
          this.secondStepSubmitted = true;

          let e4 = this.zipcode?.errors;
          let e5 = this.houseNumber?.errors;
          let e6 = this.location?.errors;
          let e7 = this.streetName?.errors;
          let e8 = this.startTime?.errors;
          let e9 = this.endTime?.errors;
          let e10 = this.activityDate?.errors;

          if (!e4 && !e5 && !e6 && !e7 && !e8 && !e9 && !e10) {
            this.currentStep++;
            this.scrollToTop();
          }
          break;
        case 3:
          this.currentStep++;
          this.scrollToTop();

          break;
      }
    } else {
      this.currentStep--;
      this.scrollToTop();
    }
  }

  /**
   * Creates an activity based on the details provided in the eventForm using the uploaded images (if any).
   *
   * If an activity was successfully created, redirects user to the 'bedankt' page.
   */
  createActivity() {
    this.submitDisabled = true;

    const imageArray = this.images.filter(e => e);
    const filesArray: File[] = [];

    imageArray.forEach(img => {
      filesArray.push(img['file']);
    });

    this.eventService
      .createEvent(this.eventForm.value, filesArray)
      .then(
        (eventID) => {
          this.router.navigateByUrl('/bedankt/' + eventID, { replaceUrl: true });
        },
        async (err) => {
          console.log(err);

          this.submitError = err;
          this.submitDisabled = false;
        }
      );
  }

  /**
   * Updates an activity based on the details provided in the eventForm.
   *
   * If an activity was successfully edited, redirects user to the event page.
   */
  editActivity() {
    this.submitDisabled = true;

    const imageArray = this.images.filter(e => e);
    const filesArray: File[] = [];

    imageArray.forEach(img => {
      filesArray.push(img['file']);
    });

    if (this.postId) {
      this.eventService
        .updateEvent(this.postId, this.postOrgRef, this.eventForm.value, filesArray)
        .then(
          (eventID) => {
            this.router.navigateByUrl('/activiteit/' + eventID, { replaceUrl: true });
          },
          async (err) => {
            console.log(err);

            this.submitError = err;
            this.submitDisabled = false;
          }
        );
    }
  }

  /**
   * This function is triggered when the user changes the zipcode or house number.
   * It retrieves the zipcode and housenumber from the form and if an address is found, it fills in the location and streetname fields.
   */
  onZipcodeOrHouseNumberChange(): void {
    const zipcode = this.eventForm.get('zipcode')?.value;
    const houseNumber = this.eventForm.get('houseNumber')?.value;

    if (zipcode && houseNumber) {
      this.geocodeService.fetchAddressFromPDOK(zipcode, houseNumber).subscribe({
        next: (address) => {
          this.eventForm.patchValue({
            streetName: address.street,
            location: address.city,
          });
        },
        error: (error) => {
          console.warn('Adres niet gevonden:', error);
        },
      });
    }
  }

  /**
   * If there is an event ID in the URL, this function gets triggered to retrieve the corresponding event from the database.
   * It then fills the event form with all the retrieved data to allow for easy editing of the event.
   *
   * @param event Event from the database to be loaded into the event form
   */
  loadPost(event: Event): void {
    for (let x = 1; x < event.parts.length; x++) {
      this.addPart();
    }

    const startDate = new Date(event.start.seconds * 1000);
    const endDate = new Date(event.end.seconds * 1000);

    const formattedDate = startDate.toISOString().split('T')[0];
    const formattedStartTime = startDate.toTimeString().split(' ')[0];
    const formattedEndTime = endDate .toTimeString().split(' ')[0];

    const transformedEvent = {
      ...event,
      ...event.address,
      ...Object.keys(event.accessibility).reduce((accessibility, key) => {
        accessibility[`${key}Check`] = <boolean>event.accessibility[key];
        return accessibility;
      }, {} as { [key: string]: boolean }),
      startTime: formattedStartTime,
      endTime: formattedEndTime,
      activityDate: formattedDate
    };

    this.postOrgRef = event.organization;
    this.eventForm.patchValue(transformedEvent);
    this.loadPostImages(event.images);
  }

  /**
   * Loads the images from the event into the image upload containers on the last part of the event form.
   *
   * @param images Retrieved event images
   */
  loadPostImages(images: [string]): void {
    for (const image of images) {
      this.imgService.retrieveBlobFromStorage(image).then(async result => {
        try {
          const base64data = await this.readFileAsDataUrl(result.blob);
          const file = new File([result.blob], result.imageName, {type: "image/jpeg"});

          this.addImage({src: base64data, file, id: this.getNextAvailableIndex()});
        } catch (e) {
          console.error(e);
        }
      });
    }
  }

  /**
   * Return the number of images already uploaded
   *
   */
  getCurrentImageCount(): number {
    return this.images.filter(image => image !== null).length;
  }

  /**
   * Find the first available index (i.e., null slot)
   */
  getNextAvailableIndex(): number {
    return this.images.findIndex(image => image === null);
  }

  /**
   * Add image to the first available slot
   *
   * @param image Image to be added to the image array
   */
  addImage(image: any): void {
    const index = this.getNextAvailableIndex();
    if (index !== -1) {
      this.images[index] = image;
    }
  }

  /**
   * Remove image from slot and shift remaining images left
   *
   * @param index Index of the image to be deleted
   */
  deleteImage(index: number): void {
    for (let i = index; i < this.images.length - 1; i++) {
      this.images[i] = this.images[i + 1]; // Shift images left
    }
    this.images[this.images.length - 1] = null; // Set the last slot to null
  }

  /**
   * Adds a new event part if there are currently less than 10.
   */
  addPart() {
    if (this.parts.length < 10) {
      this.parts.push(this.newPart());
    }
  }

  /**
   * Creates the formGroup part necessary for the eventForm.
   */
  newPart(): FormGroup {
    return this.fb.group({
      name: [null],
      start: [null],
      end: [null]
    })
  }

  /**
   * Removes the event part selected based on the index.
   *
   * @param index
   */
  removePartField(index: number): void {
    if (this.parts.length > 1) {
      this.parts.removeAt(index);
    }
  }

  /**
   * Turns the blob into base64, so that it can be used to create thumbnails for the image upload containers.
   *
   * @param blob Image blob retrieved by Firebase
   */
  readFileAsDataUrl(blob: Blob): Promise<string | ArrayBuffer | null> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = function () {
        resolve(reader.result);
      }

      reader.onerror = function () {
        reject(new Error("Er is iets foutgegaan tijdens het uitlezen van het bestand"));
      }

      reader.readAsDataURL(blob);
    });
  }

  /**
   * Scrolls the window back to the top of the screen, used when changing the form steps.
   */
  scrollToTop(): void {
    const navbarHeight = document.querySelector('nav')?.clientHeight || 90;

    // Calculate the position of the target element minus the navbar height
    const targetPosition = this.topOfPage.nativeElement.offsetTop - navbarHeight;

    // Scroll to that position
    window.scrollTo({ top: targetPosition, behavior: 'smooth' });
  }

  /**
   * @return Filled in activity parts value on activity form.
   */
  get parts() {
    return this.eventForm.controls["parts"] as FormArray;
  }

  /**
   * @return Filled in activity name value on activity form.
   */
  get name() {
    return this.eventForm.get('name');
  }

  /**
   * @return Filled in activity description value on activity form.
   */
  get description() {
    return this.eventForm.get('description');
  }

  /**
   * @return Filled in activity category value on activity form.
   */
  get category() {
    return this.eventForm.get('category');
  }

  /**
   * @return Filled in zipcode value on activity form.
   */
  get zipcode() {
    return this.eventForm.get('zipcode');
  }

  /**
   * @return Filled in house number value on activity form.
   */
  get houseNumber() {
    return this.eventForm.get('houseNumber');
  }

  /**
   * @return Filled in location value on activity form.
   */
  get location() {
    return this.eventForm.get('location');
  }

  /**
   * @return Filled in street name value on activity form.
   */
  get streetName() {
    return this.eventForm.get('streetName');
  }

  /**
   * @return Filled in start time value on activity form.
   */
  get startTime() {
    return this.eventForm.get('startTime');
  }

  /**
   * @return Filled in end time value on activity form.
   */
  get endTime() {
    return this.eventForm.get('endTime');
  }

  /**
   * @return Filled in activity date value on activity form.
   */
  get activityDate() {
    return this.eventForm.get('activityDate');
  }
}
